/**
 * Cloudflare Turnstile, loaded on demand (explicit rendering). The widget is
 * rendered into a container the dialog provides; the token is single-use.
 */

export interface TurnstileClient {
  /** Render a widget in `container` and resolve with its token. Rejects if it errors or `signal` aborts. */
  getToken(container: HTMLElement, signal: AbortSignal): Promise<string>;
}

/** Cloudflare's documented always-pass test site key (pairs with the always-pass test secret). */
export const TEST_SITE_KEY = "1x00000000000000000000AA";

interface TurnstileApi {
  render(el: HTMLElement, opts: Record<string, unknown>): string;
  remove(id: string): void;
}

declare global {
  interface Window {
    turnstile?: TurnstileApi;
  }
}

const SCRIPT_URL = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";
let loading: Promise<TurnstileApi> | null = null;

function loadScript(): Promise<TurnstileApi> {
  if (window.turnstile) return Promise.resolve(window.turnstile);
  loading ??= new Promise<TurnstileApi>((resolve, reject) => {
    const s = document.createElement("script");
    s.src = SCRIPT_URL;
    s.async = true;
    s.onload = () => {
      if (window.turnstile) resolve(window.turnstile);
      else reject(new Error("turnstile missing"));
    };
    s.onerror = () => {
      loading = null;
      reject(new Error("turnstile script failed"));
    };
    document.head.appendChild(s);
  });
  return loading;
}

export class CloudflareTurnstile implements TurnstileClient {
  constructor(
    private readonly siteKey: string,
    private readonly theme: "dark" | "light" = "dark",
  ) {}

  async getToken(container: HTMLElement, signal: AbortSignal): Promise<string> {
    const api = await loadScript();
    return new Promise<string>((resolve, reject) => {
      const id = api.render(container, {
        sitekey: this.siteKey,
        theme: this.theme,
        language: "tr",
        callback: (token: string) => {
          resolve(token);
        },
        "error-callback": () => {
          reject(new Error("turnstile error"));
        },
        "expired-callback": () => {
          reject(new Error("turnstile expired"));
        },
      });
      signal.addEventListener("abort", () => {
        api.remove(id);
        reject(new DOMException("aborted", "AbortError"));
      });
    });
  }
}

export function siteKeyFromEnv(value: string | undefined): string {
  return value && value.length > 0 ? value : TEST_SITE_KEY;
}
